import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";

/** Every step that matters for proof. Order of appearance in a normal signing is roughly top to bottom. */
export const EVENT_TYPES = [
  "envelope_created",
  "document_rendered",
  "company_signed",
  "envelope_sent",
  "email_sent",
  "email_bounced",
  "link_opened",
  "code_sent",
  "code_failed",
  "code_verified",
  "link_paused",
  "consent_given",
  "consent_withdrawn",
  "document_viewed",
  "document_downloaded",
  "offer_accepted",
  "declined",
  "form_chosen",
  "field_saved",
  "review_shown",
  "signature_adopted",
  "perjury_statement_shown",
  "signature_applied",
  "completed",
  "seal_started",
  "seal_completed",
  "seal_failed",
  "reminder_sent",
  "link_resent",
  "expiry_extended",
  "expired",
  "voided",
  "paper_chosen",
  "paper_received",
  // The form's answers were deleted after sealing: the sealed PDF is the only record.
  "answers_deleted",
  // An ID proof: a picture added or taken away (its fingerprint and size, never the picture), and the
  // loose pictures deleted once they were sealed into the ID proof's PDF.
  "file_uploaded",
  "file_removed",
  "uploads_deleted",
  // The signer finished; the company countersigns next (its company_signed events come after this).
  "signer_finished",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

export interface EventInput {
  type: EventType;
  /** ISO time, UTC. */
  at: string;
  actor: { kind: "signer" | "sender" | "system" | "api"; id?: string };
  ip?: string;
  userAgent?: string;
  documentId?: string;
  data?: { [k: string]: Json };
}

export interface ChainedEvent extends EventInput {
  envelopeId: string;
  seq: number;
  prevHash: string;
  hash: string;
}

export const GENESIS = "0".repeat(64);

/** JSON with keys sorted at every level and no undefined — the same bytes on every machine. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== "object") {
    if (typeof v === "number" && !Number.isFinite(v)) throw new Error("Non-finite number in event");
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) return `[${v.map((x) => canonicalJson(x === undefined ? null : x)).join(",")}]`;
  const keys = Object.keys(v as Record<string, unknown>).filter((k) => (v as Record<string, unknown>)[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson((v as Record<string, unknown>)[k])}`).join(",")}}`;
}

export function sha256Hex(data: string | Uint8Array): string {
  return bytesToHex(sha256(typeof data === "string" ? utf8ToBytes(data) : data));
}

function eventHash(e: Omit<ChainedEvent, "hash">): string {
  const { envelopeId, seq, prevHash, type, at, actor, ip, userAgent, documentId, data } = e;
  return sha256Hex(canonicalJson({ envelopeId, seq, prevHash, type, at, actor, ip, userAgent, documentId, data }));
}

/** The next link: the store writes it only if no event with this seq exists yet. */
export function nextEvent(prev: ChainedEvent | null, envelopeId: string, input: EventInput): ChainedEvent {
  if (prev && prev.envelopeId !== envelopeId) throw new Error("Previous event belongs to another envelope");
  const base = { ...input, envelopeId, seq: prev ? prev.seq + 1 : 1, prevHash: prev ? prev.hash : GENESIS };
  return { ...base, hash: eventHash(base) };
}

export type ChainCheck = { ok: true; head: string; count: number } | { ok: false; seq: number; reason: string };

/** Recomputes every link. Any edited, removed, reordered or inserted event shows up here. */
export function verifyChain(events: ChainedEvent[]): ChainCheck {
  let prev = GENESIS;
  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    if (e.seq !== i + 1) return { ok: false, seq: e.seq, reason: `expected event ${i + 1}, found ${e.seq}` };
    if (e.prevHash !== prev) return { ok: false, seq: e.seq, reason: "previous-hash link broken" };
    const { hash, ...rest } = e;
    if (eventHash(rest) !== hash) return { ok: false, seq: e.seq, reason: "event content changed" };
    prev = hash;
  }
  return { ok: true, head: prev, count: events.length };
}
